import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  BuildingIcon,
  FileTextIcon,
  CheckCircle2Icon,
  XCircleIcon,
  AlertCircleIcon,
  EditIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AppHeader } from '@/components/app-header'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AppointmentDialog } from '@/components/appointment-dialog'

interface Appointment {
  id: number
  patient: {
    id: number
    display_name: string
    phone_number: string
  }
  care_case_id: number
  site: {
    id: number
    name: string
  }
  appointment_type: string
  type_label: string
  scheduled_at: string
  formatted_date: string
  formatted_time: string
  formatted_datetime: string
  duration_minutes: number
  duration_hours: number
  end_time: string
  status: string
  status_label: string
  location: string | null
  reason: string | null
  notes: string | null
  cancellation_reason: string | null
  cancelled_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  upcoming: boolean
  past: boolean
}

interface AdminAppointmentsShowProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  appointment: Appointment
}

export default function AdminAppointmentsShow({ auth, appointment }: AdminAppointmentsShowProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showNotesDialog, setShowNotesDialog] = useState(false)
  const [clinicalNotes, setClinicalNotes] = useState(appointment.notes || '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'completed':
        return 'default'
      case 'confirmed':
        return 'secondary'
      case 'cancelled':
      case 'no_show':
        return 'destructive'
      case 'scheduled':
      default:
        return 'outline'
    }
  }

  const getTypeBadgeColor = (type: string): string => {
    switch (type) {
      case 'initial_consultation':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      case 'follow_up_visit':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'lab_review':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'check_in':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
      case 'phone_call':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      case 'telehealth':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  const getStatusIcon = () => {
    switch (appointment.status) {
      case 'completed':
        return <CheckCircle2Icon className="h-5 w-5 text-green-600 dark:text-green-400" />
      case 'cancelled':
      case 'no_show':
        return <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
      case 'confirmed':
        return <CheckCircle2Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      default:
        return <AlertCircleIcon className="h-5 w-5 text-muted-foreground" />
    }
  }

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    }
  }

  const handleSaveNotes = async () => {
    setIsSubmitting(true)

    try {
      const response = await fetch(`/admin/appointments/${appointment.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: clinicalNotes || null }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Clinical notes updated successfully')
        setShowNotesDialog(false)
        router.reload()
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to update notes')
      }
    } catch (error) {
      console.error('Error updating notes:', error)
      toast.error('An error occurred while updating notes')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Appointments', href: '/admin/appointments' },
          { label: appointment.formatted_date },
        ]}
        actions={
          <Button size="sm" variant="outline" onClick={() => router.visit('/admin/appointments')}>
            Back to Appointments
          </Button>
        }
      />

      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* Hero Card */}
          <Card className="border-2">
            <CardHeader className="bg-muted/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={getTypeBadgeColor(appointment.appointment_type)}>
                      {appointment.type_label}
                    </Badge>
                    <Badge variant={getStatusBadgeVariant(appointment.status)}>
                      <span className="flex items-center gap-1">
                        {getStatusIcon()}
                        {appointment.status_label}
                      </span>
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl">{appointment.formatted_datetime}</CardTitle>
                  <CardDescription className="mt-2 flex items-center gap-2">
                    <ClockIcon className="h-4 w-4" />
                    Duration: {appointment.duration_minutes} minutes
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Patient Information */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Patient</h3>
                  <div
                    className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3 cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => router.visit(`/admin/patients/${appointment.patient.id}`)}
                  >
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{appointment.patient.display_name}</div>
                      <div className="text-sm text-muted-foreground">{appointment.patient.phone_number}</div>
                    </div>
                  </div>
                </div>

                {/* Site Information */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Location</h3>
                  <div className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
                    <BuildingIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{appointment.site.name}</div>
                      {appointment.location && (
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPinIcon className="h-3 w-3" />
                          {appointment.location}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Details Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Appointment Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Appointment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {appointment.reason && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Reason</div>
                    <div className="text-sm">{appointment.reason}</div>
                  </div>
                )}

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Time:</span>
                    <span className="font-medium">{appointment.formatted_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">{appointment.duration_minutes} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span className="font-medium">{new Date(appointment.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes & Status Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileTextIcon className="h-5 w-5" />
                    Additional Information
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setClinicalNotes(appointment.notes || '')
                      setShowNotesDialog(true)
                    }}
                  >
                    <EditIcon className="h-4 w-4 mr-1" />
                    {appointment.notes ? 'Edit Notes' : 'Add Notes'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {appointment.notes && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Clinical Notes</div>
                    <div className="text-sm rounded-lg bg-muted/50 p-3 whitespace-pre-wrap">
                      {appointment.notes}
                    </div>
                  </div>
                )}

                {appointment.cancellation_reason && (
                  <div>
                    <div className="text-sm font-medium text-destructive mb-1">Cancellation Reason</div>
                    <div className="text-sm rounded-lg bg-destructive/10 p-3 whitespace-pre-wrap">
                      {appointment.cancellation_reason}
                    </div>
                    {appointment.cancelled_at && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Cancelled on {new Date(appointment.cancelled_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}

                {appointment.completed_at && (
                  <div>
                    <div className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">
                      Completed
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(appointment.completed_at).toLocaleString()}
                    </div>
                  </div>
                )}

                {!appointment.notes && !appointment.cancellation_reason && !appointment.completed_at && (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No additional information
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => router.visit(`/admin/patients/${appointment.patient.id}`)}
                >
                  View Patient Details
                </Button>
                {appointment.upcoming && appointment.status !== 'cancelled' && (
                  <Button variant="outline" onClick={() => setShowEditDialog(true)}>
                    Edit Appointment
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <AppointmentDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        appointment={appointment}
        mode="edit"
      />

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clinical Notes</DialogTitle>
            <DialogDescription>
              Add or update clinical notes for this appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clinical_notes">Clinical Notes</Label>
              <Textarea
                id="clinical_notes"
                value={clinicalNotes}
                onChange={(e) => setClinicalNotes(e.target.value)}
                placeholder="Enter clinical notes, observations, or any relevant information about this appointment..."
                rows={8}
              />
              <div className="text-xs text-muted-foreground">
                {clinicalNotes.length} characters
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNotesDialog(false)
                setClinicalNotes(appointment.notes || '')
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveNotes} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
